import { useCallback, useRef, useState } from 'react';

import { useGetStaticAssetUrl } from '@change-corgi/core-react/assets';
import { useTracking } from '@change-corgi/core-react/tracking';

import { usePauseOnHidden } from '#src/app/shared/hooks/dom';
import { isMotionReduced } from '#src/app/shared/utils/accessibility';

import type { QuestionNumber } from '../../constants';
import { QUESTION_TIMESTAMPS_SECONDS, VIDEO_ASSET_PATHNAME } from '../../constants';
import { useExpandedQuestion } from '../useExpandedQuestion';
import { usePromoteMutedVideoOnClick } from '../usePromoteMutedVideoOnClick';
import { useVideoScrollTriggeredAutoplay } from '../useVideoScrollTriggeredAutoplay';

type Data = {
	// A ref *callback*, not a plain RefObject: useVideoScrollTriggeredAutoplay needs to react to
	// the <video> node attaching, which a plain ref's `.current` mutation can't signal to other hooks.
	videoRef: React.RefCallback<HTMLVideoElement>;
	sectionRef: React.RefObject<HTMLElement | null>;
	// whether the "click to play" overlay should be shown (initially, and again once the video ends)
	showOverlay: boolean;
	// whether the video has ever been started — used to enable native controls once playback has begun
	hasStarted: boolean;
	// full static.change.org URL of the intro video, or '' while the asset hasn't been uploaded yet
	videoSrc: string;
	// which question's answer is expanded, or null if all are collapsed
	expandedQuestion: QuestionNumber | null;
};

type Actions = {
	handlePlayClick: () => void;
	// see usePromoteMutedVideoOnClick
	handleVideoClick: () => void;
	handleQuestionClick: (questionNumber: QuestionNumber) => void;
	handleQuestionToggle: (questionNumber: QuestionNumber, expanded: boolean) => void;
	handleEnded: () => void;
};

type Result = ModelHookResult<Data, Actions>;

// oxlint-disable-next-line max-statements -- exceeds the 18-statement limit; flagged as documented debt rather than refactored, per team decision
export function useVideoWalkthrough(): Result {
	const track = useTracking();
	const getStaticAssetUrl = useGetStaticAssetUrl();

	// videoElementRef: imperative reads (play/pause/muted/currentTime). videoElement (state): lets
	// useVideoScrollTriggeredAutoplay's observer effect react when the node attaches. videoRef
	// keeps both in sync.
	const videoElementRef = useRef<HTMLVideoElement | null>(null);
	const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(null);
	const videoRef = useCallback((node: HTMLVideoElement | null) => {
		videoElementRef.current = node;
		setVideoElement(node);
	}, []);

	const sectionRef = useRef<HTMLElement>(null);

	// Stop playback (and the segment fetching that goes with it) when the tab is backgrounded —
	// this is a ~5 minute 32MB asset, so leaving it streaming to nobody costs the user bandwidth
	// and us CDN egress. Does not auto-resume; the user re-initiates.
	usePauseOnHidden(videoElementRef);

	// Resolved per-environment (static.change.org in prod, static.staging-change.org in staging, …).
	// Stays '' until the asset is uploaded, so the video renders poster-only rather than
	// requesting a URL that would 404.
	// oxlint-disable-next-line typescript/no-unnecessary-condition -- VIDEO_ASSET_PATHNAME is currently a real path, but it's intentionally set to '' until the asset is uploaded (see comment above)
	const videoSrc = VIDEO_ASSET_PATHNAME ? getStaticAssetUrl(VIDEO_ASSET_PATHNAME) : '';

	const [showOverlay, setShowOverlay] = useState(true);
	const [hasStarted, setHasStarted] = useState(false);
	// true once a scroll-triggered autoplay attempt has succeeded only by falling back to a muted
	// <video> (the unmuted attempt was rejected). A click anywhere that counts as a user gesture
	// then promotes it back to sound — see handlePlayClick / handleVideoClick / handleQuestionClick.
	const [startedMuted, setStartedMuted] = useState(false);

	// Attempts playback and reports whether it actually began. Never throws or leaves an unhandled
	// rejection: an unplayable source (e.g. the asset not being uploaded yet) resolves to false so
	// callers can put the play affordance back instead of leaving a dead surface behind.
	const playVideo = useCallback(async (): Promise<boolean> => {
		const video = videoElementRef.current;
		if (!video) return false;
		try {
			await video.play();
			return true;
		} catch {
			return false;
		}
	}, []);

	// Hides the overlay optimistically so the click feels instant, then reverts if playback never
	// started. Without the revert the overlay disappears over a video that isn't playing, and
	// `ambassadors_video_play` records a play that never happened.
	const startPlayback = useCallback(
		(onStarted?: () => void) => {
			setHasStarted(true);
			setShowOverlay(false);

			void (async () => {
				if (await playVideo()) {
					onStarted?.();
					return;
				}
				setHasStarted(false);
				setShowOverlay(true);
			})();
		},
		[playVideo],
	);

	const { handlePlayClick, handleVideoClick } = usePromoteMutedVideoOnClick({
		videoRef: videoElementRef,
		startedMuted,
		onPromoted: useCallback(() => setStartedMuted(false), []),
		startPlayback,
	});

	const handleQuestionClick = useCallback(
		(questionNumber: QuestionNumber) => {
			const video = videoElementRef.current;
			// Same reality check as usePromoteMutedVideoOnClick: startedMuted alone doesn't prove the
			// video is still muted, so gate the forced-unmute on its actual state too — otherwise a
			// chapter click could force sound back on over a mute the user set deliberately.
			const shouldPromote = startedMuted && video?.muted !== false;
			if (video) {
				if (shouldPromote) {
					video.muted = false;
				}
				video.currentTime = QUESTION_TIMESTAMPS_SECONDS[questionNumber];
			}
			if (shouldPromote) {
				setStartedMuted(false);
			}
			startPlayback();
			sectionRef.current?.scrollIntoView({ behavior: isMotionReduced() ? 'auto' : 'smooth', block: 'start' });
			// Tracked regardless of whether playback began: which questions people click is the signal
			// here, and the seek itself did happen even if the asset then failed to play.
			void track('ambassadors_video_seek', { video_question_number: questionNumber });
		},
		[startedMuted, startPlayback, track],
	);

	const { expandedQuestion, handleQuestionToggle } = useExpandedQuestion();

	const handleEnded = useCallback(() => {
		setShowOverlay(true);
		void track('ambassadors_video_complete');
	}, [track]);

	// Same overlay/started bookkeeping startPlayback does for an explicit click. Tracked as a
	// distinct event from ambassadors_video_play — autoplay is not a volitional play, and merging
	// the two would understate explicit engagement.
	const handleAutoplayStarted = useCallback(
		(autoplayStartedMuted: boolean) => {
			setHasStarted(true);
			setShowOverlay(false);
			setStartedMuted(autoplayStartedMuted);
			void track('ambassadors_video_autoplay', { autoplay_muted: autoplayStartedMuted });
		},
		[track],
	);

	useVideoScrollTriggeredAutoplay({
		videoElement,
		videoElementRef,
		playVideo,
		hasStarted,
		onAutoplayStarted: handleAutoplayStarted,
	});

	return {
		data: { videoRef, sectionRef, showOverlay, hasStarted, videoSrc, expandedQuestion },
		actions: {
			handlePlayClick,
			handleVideoClick,
			handleQuestionClick,
			handleQuestionToggle,
			handleEnded,
		},
	};
}
